import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { t } from "@/lib/i18n";
import { toast } from "sonner";
import {
  Rocket, RefreshCw, Copy, Instagram, Facebook, Music2,
  Send, Linkedin, MessageCircle, Users, Twitter, Loader2,
} from "lucide-react";

const CHANNELS = [
  { id: "instagram", label: "Instagram",  Icon: Instagram, kind: "caption" },
  { id: "tiktok",    label: "TikTok",     Icon: Music2,    kind: "caption" },
  { id: "pinterest", label: "Pinterest",  Icon: Rocket,    kind: "pin" },
  { id: "whatsapp",  label: "WhatsApp",   Icon: MessageCircle, kind: "message" },
  { id: "facebook",  label: "Facebook",   Icon: Facebook,  kind: "post" },
  { id: "x",         label: "X (Twitter)",Icon: Twitter,   kind: "tweet" },
  { id: "linkedin",  label: "LinkedIn",   Icon: Linkedin,  kind: "post" },
  { id: "reddit",    label: "Reddit",     Icon: Users,     kind: "reddit" },
];

export default function GrowthStudioPage() {
  const { lang } = useLang();
  const [products, setProducts] = useState([]);
  const [kits, setKits] = useState({}); // sid -> full kit doc
  const [selectedSid, setSelectedSid] = useState(null);
  const [busySid, setBusySid] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [activeChannel, setActiveChannel] = useState("instagram");
  const [postLang, setPostLang] = useState(lang || "en");

  useEffect(() => {
    api.get("/shopify/products?limit=200").then((r) => {
      const rows = (r.data.products || []).filter((p) => p.status === "active");
      setProducts(rows);
      if (!selectedSid && rows.length) setSelectedSid(rows[0].shopify_id);
    });
    api.get("/growth/kits").then((r) => {
      const map = {};
      (r.data.kits || []).forEach((k) => (map[k.shopify_id] = k));
      setKits(map);
    });
  }, []);

  useEffect(() => {
    if (!selectedSid) return;
    if (kits[selectedSid]?.channels) return;
    api.get(`/growth/kit/${selectedSid}`)
      .then((r) => setKits((prev) => ({ ...prev, [selectedSid]: r.data })))
      .catch(() => {/* not generated yet */});
  }, [selectedSid]);

  const activeKit = kits[selectedSid];
  const activeChannelData = useMemo(
    () => activeKit?.channels?.[activeChannel]?.[postLang] || null,
    [activeKit, activeChannel, postLang],
  );

  const generateOne = async (sid) => {
    setBusySid(sid);
    // The LLM call renders 5 langs × 8 channels; end-to-end takes ~30–60s.
    // Cloudflare may return 502 before we get the response — poll GET until
    // the doc appears or 180s elapses. This gives the operator a reliable UX
    // regardless of edge-node timeouts.
    const start = Date.now();
    const currentGeneratedAt = kits[sid]?.generated_at || null;
    const pollForFresh = async () => {
      while (Date.now() - start < 180000) {
        await new Promise((r) => setTimeout(r, 4000));
        try {
          const { data } = await api.get(`/growth/kit/${sid}`);
          if (data?.generated_at && data.generated_at !== currentGeneratedAt) {
            setKits((prev) => ({ ...prev, [sid]: data }));
            return true;
          }
        } catch { /* keep polling */ }
      }
      return false;
    };
    try {
      const req = api.post(`/growth/kit/${sid}`, null, { timeout: 180000 });
      const poll = pollForFresh();
      // Race: whichever resolves first, we win.
      const winner = await Promise.race([req, poll]);
      if (winner && winner.data) {
        setKits((prev) => ({ ...prev, [sid]: winner.data }));
      } else {
        // poll already updated state
      }
      toast.success(t(lang, "growth_kit_ready"));
    } catch (e) {
      // Even on error, the backend may have persisted — do one final refresh.
      try {
        const { data } = await api.get(`/growth/kit/${sid}`);
        if (data?.generated_at && data.generated_at !== currentGeneratedAt) {
          setKits((prev) => ({ ...prev, [sid]: data }));
          toast.success(t(lang, "growth_kit_ready"));
          return;
        }
      } catch { /* ignore */ }
      toast.error(e?.response?.data?.detail || "Failed to generate kit");
    } finally {
      setBusySid(null);
    }
  };

  const bulkGenerate = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/growth/bulk-generate", { only_missing: true, limit: 20 });
      toast.success(`${data.generated} kits generated, ${data.skipped} skipped`);
      // Reload compact list + refresh selected
      const r = await api.get("/growth/kits");
      const map = { ...kits };
      (r.data.kits || []).forEach((k) => { map[k.shopify_id] = { ...map[k.shopify_id], ...k }; });
      setKits(map);
      if (selectedSid) {
        try {
          const d = await api.get(`/growth/kit/${selectedSid}`);
          setKits((prev) => ({ ...prev, [selectedSid]: d.data }));
        } catch { /* ignore */ }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk generate failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const copyText = async (text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t(lang, "growth_copied"));
    } catch {
      window.prompt("Copy this text:", text);
    }
  };

  const buildCopyPayload = (channelId, data) => {
    if (!data) return "";
    if (channelId === "instagram" || channelId === "tiktok") {
      return `${data.caption || ""}\n\n${(data.hashtags || []).join(" ")}`.trim();
    }
    if (channelId === "pinterest") {
      return `${data.pin_title || ""}\n${data.pin_description || ""}`.trim();
    }
    if (channelId === "whatsapp") return data.message || "";
    if (channelId === "facebook") return data.post || "";
    if (channelId === "x") return data.tweet || "";
    if (channelId === "linkedin") return data.post || "";
    if (channelId === "reddit") {
      const subs = (data.subreddits || []).join(", ");
      return `TITLE: ${data.title || ""}\n\n${data.body || ""}\n\nSuggested subreddits: ${subs}`.trim();
    }
    return JSON.stringify(data);
  };

  return (
    <div className="space-y-6" data-testid="growth-studio-page">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display font-light text-3xl tracking-tight">
            {t(lang, "growth_title")}
          </h1>
          <p className="mt-1 text-white/50 text-sm max-w-2xl">
            {t(lang, "growth_subtitle")}
          </p>
        </div>
        <button
          data-testid="growth-bulk-btn"
          onClick={bulkGenerate}
          disabled={bulkBusy}
          className="hq-btn hq-btn-cyan disabled:opacity-50"
        >
          {bulkBusy ? <Loader2 size={12} className="animate-spin" /> : <Rocket size={12} />}
          {bulkBusy ? t(lang, "growth_generating") : t(lang, "growth_bulk_generate")}
        </button>
      </div>

      <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
        {/* Product picker */}
        <aside className="hq-card p-3 max-h-[70vh] overflow-y-auto">
          <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 px-2 pb-2">
            {t(lang, "growth_products")} · {products.length}
          </div>
          <div className="space-y-1">
            {products.map((p) => {
              const has = !!kits[p.shopify_id];
              const active = selectedSid === p.shopify_id;
              return (
                <button
                  key={p.shopify_id}
                  data-testid={`growth-product-${p.shopify_id}`}
                  onClick={() => setSelectedSid(p.shopify_id)}
                  className={`w-full text-left px-3 py-2 rounded-md border text-xs flex items-center gap-2 transition-colors ${
                    active
                      ? "border-[color:var(--hq-cyan)] bg-[color:var(--hq-cyan)]/[0.08]"
                      : "border-white/10 hover:border-white/30"
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                    has ? "bg-[color:var(--hq-success)]" : "bg-white/20"
                  }`} />
                  <span className="truncate">{p.title}</span>
                </button>
              );
            })}
            {products.length === 0 && (
              <div className="text-center py-8 text-white/40 text-xs">
                {t(lang, "growth_no_products")}
              </div>
            )}
          </div>
        </aside>

        {/* Kit viewer */}
        <section className="hq-card p-5 min-h-[400px]">
          {!selectedSid ? (
            <div className="text-center py-16 text-white/40 text-sm">
              {t(lang, "growth_pick_product")}
            </div>
          ) : !activeKit?.channels ? (
            <div className="text-center py-16 space-y-4">
              <div className="text-white/60 text-sm">{t(lang, "growth_not_generated")}</div>
              <button
                data-testid="growth-generate-btn"
                onClick={() => generateOne(selectedSid)}
                disabled={busySid === selectedSid}
                className="hq-btn hq-btn-primary disabled:opacity-50"
              >
                {busySid === selectedSid ? (
                  <><Loader2 size={12} className="animate-spin" /> {t(lang, "growth_generating")}</>
                ) : (
                  <><Rocket size={12} /> {t(lang, "growth_generate_one")}</>
                )}
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-baseline justify-between gap-3 flex-wrap mb-4">
                <div>
                  <div className="font-display text-lg">{activeKit.title}</div>
                  <div className="font-mono text-[10px] uppercase text-white/40 mt-1">
                    CHF {(activeKit.price_chf ?? 0).toFixed(2)} · {activeKit.handle}
                  </div>
                </div>
                <button
                  data-testid="growth-regenerate-btn"
                  onClick={() => generateOne(selectedSid)}
                  disabled={busySid === selectedSid}
                  className="hq-btn hq-btn-ghost text-xs disabled:opacity-50"
                >
                  {busySid === selectedSid ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
                  {t(lang, "growth_regenerate")}
                </button>
              </div>

              {/* Language tabs */}
              <div className="flex gap-1 mb-3 flex-wrap">
                {["pt", "en", "de", "fr", "it"].map((l) => (
                  <button
                    key={l}
                    data-testid={`growth-lang-${l}`}
                    onClick={() => setPostLang(l)}
                    className={`text-[10px] font-mono uppercase px-2.5 py-1 rounded-md border ${
                      postLang === l
                        ? "border-[color:var(--hq-cyan)] bg-[color:var(--hq-cyan)]/[0.08] text-white"
                        : "border-white/10 text-white/50 hover:border-white/30"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>

              {/* Channel tabs */}
              <div className="flex gap-1 mb-4 flex-wrap">
                {CHANNELS.map((c) => (
                  <button
                    key={c.id}
                    data-testid={`growth-channel-${c.id}`}
                    onClick={() => setActiveChannel(c.id)}
                    className={`text-xs px-3 py-1.5 rounded-md border flex items-center gap-1.5 ${
                      activeChannel === c.id
                        ? "border-[color:var(--hq-cyan)] bg-[color:var(--hq-cyan)]/[0.08] text-white"
                        : "border-white/10 text-white/60 hover:border-white/30"
                    }`}
                  >
                    <c.Icon size={12} />
                    {c.label}
                  </button>
                ))}
              </div>

              {/* Post preview */}
              <div className="rounded-lg border border-white/10 bg-black/40 p-4 space-y-3">
                {activeChannelData ? (
                  <ChannelPreview
                    channel={activeChannel}
                    data={activeChannelData}
                    productUrl={activeKit.product_url}
                    onCopy={() =>
                      copyText(buildCopyPayload(activeChannel, activeChannelData))
                    }
                    copyLabel={t(lang, "growth_copy")}
                  />
                ) : (
                  <div className="text-white/40 text-xs">{t(lang, "growth_no_content")}</div>
                )}
              </div>
              <div className="mt-3 text-[10px] font-mono uppercase text-white/40">
                {t(lang, "growth_tip")}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

function ChannelPreview({ channel, data, productUrl, onCopy, copyLabel }) {
  return (
    <div className="space-y-3">
      {channel === "instagram" || channel === "tiktok" ? (
        <>
          <div className="text-white/90 text-sm whitespace-pre-wrap leading-relaxed">
            {data.caption}
          </div>
          <div className="flex flex-wrap gap-1">
            {(data.hashtags || []).map((h) => (
              <span key={h} className="text-[10px] font-mono text-[color:var(--hq-cyan)]">{h}</span>
            ))}
          </div>
        </>
      ) : channel === "pinterest" ? (
        <>
          <div className="text-white text-sm font-medium">{data.pin_title}</div>
          <div className="text-white/70 text-xs whitespace-pre-wrap leading-relaxed">
            {data.pin_description}
          </div>
        </>
      ) : channel === "whatsapp" ? (
        <div className="text-white/90 text-sm whitespace-pre-wrap leading-relaxed">
          {data.message}
        </div>
      ) : channel === "facebook" ? (
        <div className="text-white/90 text-sm whitespace-pre-wrap leading-relaxed">{data.post}</div>
      ) : channel === "x" ? (
        <div className="text-white/90 text-sm">{data.tweet}</div>
      ) : channel === "linkedin" ? (
        <div className="text-white/90 text-sm whitespace-pre-wrap leading-relaxed">{data.post}</div>
      ) : channel === "reddit" ? (
        <>
          <div className="text-white text-sm font-medium">{data.title}</div>
          <div className="text-white/70 text-xs whitespace-pre-wrap leading-relaxed">
            {data.body}
          </div>
          {data.subreddits?.length ? (
            <div className="flex flex-wrap gap-1 pt-1">
              {data.subreddits.map((s) => (
                <span key={s} className="text-[10px] font-mono text-white/50 border border-white/10 rounded px-1.5 py-0.5">
                  {s}
                </span>
              ))}
            </div>
          ) : null}
        </>
      ) : null}

      <div className="flex items-center justify-between pt-2 border-t border-white/10">
        <a
          href={productUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[10px] font-mono text-white/40 truncate max-w-[60%]"
        >
          {productUrl}
        </a>
        <button
          data-testid="growth-copy-btn"
          onClick={onCopy}
          className="hq-btn hq-btn-primary text-xs"
        >
          <Copy size={12} /> {copyLabel}
        </button>
      </div>
    </div>
  );
}
